import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const MV_STRATEGIES = new Set(['market_value', 'sb_baseline']);

function FiltersModal({ filters, onApply, onClose, maxBookmakers, bookmakers, bookmakerLogos, bookmakerNames, strategy }) {
    const isMV = MV_STRATEGIES.has(strategy);
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleMinBookmakersToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minBookmakers: {
                ...prev.minBookmakers,
                enabled: !prev.minBookmakers.enabled,
            }
        }));
    };

    const handleMinBookmakersChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minBookmakers: {
                ...prev.minBookmakers,
                value: Number(value),
            }
        }));
    };

    const handleExcludeBestToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            excludeBestBookmakers: {
                ...prev.excludeBestBookmakers,
                enabled: !prev.excludeBestBookmakers.enabled,
            }
        }));
    };

    const handleMinEvToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minEv: { ...prev.minEv, enabled: !prev.minEv.enabled }
        }));
    };

    const handleMinEvChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minEv: { ...prev.minEv, value: Number(value) }
        }));
    };

    const handleMinArbToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            minArb: { ...prev.minArb, enabled: !prev.minArb.enabled }
        }));
    };

    const handleMinArbChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            minArb: { ...prev.minArb, value: Number(value) }
        }));
    };

    const handleArbExcludeToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            excludeBookmakers: {
                ...prev.excludeBookmakers,
                enabled: !prev.excludeBookmakers.enabled,
            }
        }));
    };

    const handleArbExcludeBookmakerClick = (bookmaker) => {
        setLocalFilters(prev => {
            const current = prev.excludeBookmakers.value;
            const updated = current.includes(bookmaker)
                ? current.filter(b => b !== bookmaker)
                : [...current, bookmaker];
            return {
                ...prev,
                excludeBookmakers: {
                    ...prev.excludeBookmakers,
                    value: updated,
                }
            };
        });
    };

    const handleExcludeBookmakerClick = (bookmaker) => {
        setLocalFilters(prev => {
            const current = prev.excludeBestBookmakers.value;
            const updated = current.includes(bookmaker)
                ? current.filter(b => b !== bookmaker)
                : [...current, bookmaker];
            return {
                ...prev,
                excludeBestBookmakers: {
                    ...prev.excludeBestBookmakers,
                    value: updated,
                }
            };
        });
    };

    const handleStartWithinToggle = () => {
        setLocalFilters(prev => ({
            ...prev,
            startWithin: { ...prev.startWithin, enabled: !prev.startWithin.enabled }
        }));
    };

    const handleStartWithinChange = (value) => {
        setLocalFilters(prev => ({
            ...prev,
            startWithin: { ...prev.startWithin, value: Number(value) }
        }));
    };

    const handleApply = () => {
        onApply(localFilters);
        onClose();
    };

    const handleReset = () => {
        let defaults;
        if (strategy === 'arbs') {
            defaults = {
                minArb: { enabled: true, value: 2 },
                excludeBookmakers: { enabled: false, value: [] },
            };
        } else if (strategy === 'sb_baseline') {
            defaults = {
                minBookmakers: { enabled: false, value: 2 },
                minEv: { enabled: true, value: 10 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        } else if (isMV) {
            defaults = {
                minBookmakers: { enabled: true, value: 4 },
                minEv: { enabled: true, value: 10.5 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        } else {
            defaults = {
                minBookmakers: { enabled: true, value: 5 },
                minEv: { enabled: true, value: 3 },
                startWithin: { enabled: false, value: 3 },
                excludeBestBookmakers: { enabled: false, value: [] },
            };
        }
        setLocalFilters(defaults);
    };

    const activeCount = Object.entries(localFilters).filter(([, f]) => {
        if (!f.enabled) return false;
        if (Array.isArray(f.value)) return f.value.length > 0;
        return true;
    }).length;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-md bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-700/50">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-white">Filters</h3>
                            <p className="text-xs text-slate-500">
                                {activeCount > 0 ? `${activeCount} active` : 'No filters active'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto no-scrollbar">
                    {/* Minimum Bookmakers (EV / Market Value only, not SB Baseline) */}
                    {strategy !== 'arbs' && strategy !== 'sb_baseline' && (
                    <div className={`p-4 rounded-xl border transition-colors ${
                        localFilters.minBookmakers?.enabled
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-slate-800/30 border-slate-700/30'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-medium text-white">Minimum Bookmakers</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Only show bets with at least this many bookmakers in the sample
                                </div>
                            </div>
                            <button
                                onClick={handleMinBookmakersToggle}
                                className={`relative w-10 h-5.5 rounded-full transition-colors ${
                                    localFilters.minBookmakers?.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                                }`}
                                style={{ minWidth: '40px', height: '22px' }}
                            >
                                <div
                                    className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow transition-transform ${
                                        localFilters.minBookmakers?.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`}
                                    style={{ width: '18px', height: '18px' }}
                                />
                            </button>
                        </div>

                        {localFilters.minBookmakers?.enabled && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-slate-500">Min bookmakers</span>
                                    <span className="text-sm font-mono font-semibold text-emerald-400">
                                        {localFilters.minBookmakers.value}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={2}
                                    max={maxBookmakers}
                                    value={localFilters.minBookmakers.value}
                                    onChange={(e) => handleMinBookmakersChange(e.target.value)}
                                    className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-emerald-500
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer
                                        [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600 [&::-moz-range-thumb]:cursor-pointer"
                                />
                                <div className="flex justify-between mt-1">
                                    <span className="text-[10px] text-slate-600">2</span>
                                    <span className="text-[10px] text-slate-600">{maxBookmakers}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Minimum Arb% (Arbs only) */}
                    {strategy === 'arbs' && (
                    <div className={`p-4 rounded-xl border transition-colors ${
                        localFilters.minArb?.enabled
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-slate-800/30 border-slate-700/30'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-medium text-white">Minimum Arb%</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Only show arbitrage opportunities with at least this percentage
                                </div>
                            </div>
                            <button
                                onClick={handleMinArbToggle}
                                className={`relative rounded-full transition-colors ${
                                    localFilters.minArb?.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                                }`}
                                style={{ minWidth: '40px', height: '22px' }}
                            >
                                <div
                                    className={`absolute top-0.5 rounded-full bg-white shadow transition-transform ${
                                        localFilters.minArb?.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`}
                                    style={{ width: '18px', height: '18px' }}
                                />
                            </button>
                        </div>

                        {localFilters.minArb?.enabled && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-slate-500">Min Arb%</span>
                                    <span className="text-sm font-mono font-semibold text-emerald-400">
                                        {localFilters.minArb.value}%
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={1}
                                    max={10}
                                    step={0.5}
                                    value={localFilters.minArb.value}
                                    onChange={(e) => handleMinArbChange(e.target.value)}
                                    className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-emerald-500
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer
                                        [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600 [&::-moz-range-thumb]:cursor-pointer"
                                />
                                <div className="flex justify-between mt-1">
                                    <span className="text-[10px] text-slate-600">1%</span>
                                    <span className="text-[10px] text-slate-600">10%</span>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Minimum EV% (EV / Market Value only) */}
                    {strategy !== 'arbs' && (
                    <div className={`p-4 rounded-xl border transition-colors ${
                        localFilters.minEv?.enabled
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-slate-800/30 border-slate-700/30'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-medium text-white">Minimum {isMV ? 'Edge' : 'EV'}%</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Only show bets with at least this {isMV ? 'edge' : 'expected value'}
                                </div>
                            </div>
                            <button
                                onClick={handleMinEvToggle}
                                className={`relative rounded-full transition-colors ${
                                    localFilters.minEv?.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                                }`}
                                style={{ minWidth: '40px', height: '22px' }}
                            >
                                <div
                                    className={`absolute top-0.5 rounded-full bg-white shadow transition-transform ${
                                        localFilters.minEv?.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`}
                                    style={{ width: '18px', height: '18px' }}
                                />
                            </button>
                        </div>

                        {localFilters.minEv?.enabled && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-slate-500">Min {isMV ? 'Edge' : 'EV'}%</span>
                                    <span className="text-sm font-mono font-semibold text-emerald-400">
                                        +{localFilters.minEv.value}%
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={strategy === 'sb_baseline' ? 10 : isMV ? 10.5 : 3}
                                    max={isMV ? 30 : 20}
                                    step={strategy === 'sb_baseline' ? 1 : isMV ? 0.5 : 1}
                                    value={localFilters.minEv.value}
                                    onChange={(e) => handleMinEvChange(e.target.value)}
                                    className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-emerald-500
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer
                                        [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600 [&::-moz-range-thumb]:cursor-pointer"
                                />
                                <div className="flex justify-between mt-1">
                                    <span className="text-[10px] text-slate-600">{strategy === 'sb_baseline' ? '10' : isMV ? '10.5' : '3'}%</span>
                                    <span className="text-[10px] text-slate-600">{isMV ? '30' : '20'}%</span>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Exclude Bookmakers (Arbs only) */}
                    {strategy === 'arbs' && (
                    <div className={`p-4 rounded-xl border transition-colors ${
                        localFilters.excludeBookmakers?.enabled
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-slate-800/30 border-slate-700/30'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-medium text-white">Exclude Bookmakers</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Hide arbs involving any of the selected bookmakers
                                </div>
                            </div>
                            <button
                                onClick={handleArbExcludeToggle}
                                className={`relative rounded-full transition-colors ${
                                    localFilters.excludeBookmakers?.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                                }`}
                                style={{ minWidth: '40px', height: '22px' }}
                            >
                                <div
                                    className={`absolute top-0.5 rounded-full bg-white shadow transition-transform ${
                                        localFilters.excludeBookmakers?.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`}
                                    style={{ width: '18px', height: '18px' }}
                                />
                            </button>
                        </div>

                        {localFilters.excludeBookmakers?.enabled && bookmakers && bookmakers.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-2.5">
                                    Select bookmakers to exclude
                                    {localFilters.excludeBookmakers.value.length > 0 && (
                                        <span className="text-emerald-400 ml-1">
                                            ({localFilters.excludeBookmakers.value.length} selected)
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {bookmakers.map(bk => {
                                        const isExcluded = localFilters.excludeBookmakers.value.includes(bk);
                                        const logo = bookmakerLogos?.[bk];
                                        const name = bookmakerNames?.[bk] || bk;
                                        return (
                                            <button
                                                key={bk}
                                                onClick={() => handleArbExcludeBookmakerClick(bk)}
                                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                                    isExcluded
                                                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                                        : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600'
                                                }`}
                                                title={isExcluded ? `Excluding ${name}` : name}
                                            >
                                                {logo ? (
                                                    <img
                                                        src={logo}
                                                        alt={name}
                                                        className={`w-5 h-5 rounded object-cover ${isExcluded ? 'opacity-50' : ''}`}
                                                    />
                                                ) : (
                                                    <span>{name}</span>
                                                )}
                                                {isExcluded && (
                                                    <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Match Start Time (EV / Market Value only) */}
                    {strategy !== 'arbs' && (
                    <div className={`p-4 rounded-xl border transition-colors ${
                        localFilters.startWithin?.enabled
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-slate-800/30 border-slate-700/30'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-medium text-white">Match Start Time</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Only show games starting within this time window
                                </div>
                            </div>
                            <button
                                onClick={handleStartWithinToggle}
                                className={`relative rounded-full transition-colors ${
                                    localFilters.startWithin?.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                                }`}
                                style={{ minWidth: '40px', height: '22px' }}
                            >
                                <div
                                    className={`absolute top-0.5 rounded-full bg-white shadow transition-transform ${
                                        localFilters.startWithin?.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`}
                                    style={{ width: '18px', height: '18px' }}
                                />
                            </button>
                        </div>

                        {localFilters.startWithin?.enabled && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-slate-500">Starts within</span>
                                    <span className="text-sm font-mono font-semibold text-emerald-400">
                                        {localFilters.startWithin.value < 1
                                            ? `${localFilters.startWithin.value * 60} mins`
                                            : `${localFilters.startWithin.value} ${localFilters.startWithin.value === 1 ? 'hour' : 'hours'}`}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={0.5}
                                    max={5}
                                    step={0.5}
                                    value={localFilters.startWithin.value}
                                    onChange={(e) => handleStartWithinChange(e.target.value)}
                                    className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-emerald-500
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-emerald-600 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer
                                        [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-emerald-600 [&::-moz-range-thumb]:cursor-pointer"
                                />
                                <div className="flex justify-between mt-1">
                                    <span className="text-[10px] text-slate-600">30m</span>
                                    <span className="text-[10px] text-slate-600">5hrs</span>
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Exclude Best Bookmakers (EV / Market Value only) */}
                    {strategy !== 'arbs' && (
                    <div className={`p-4 rounded-xl border transition-colors ${
                        localFilters.excludeBestBookmakers?.enabled
                            ? 'bg-emerald-500/5 border-emerald-500/20'
                            : 'bg-slate-800/30 border-slate-700/30'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-sm font-medium text-white">Exclude Best Bookmakers</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    Hide bets where the best odds come from a selected bookmaker
                                </div>
                            </div>
                            <button
                                onClick={handleExcludeBestToggle}
                                className={`relative rounded-full transition-colors ${
                                    localFilters.excludeBestBookmakers.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                                }`}
                                style={{ minWidth: '40px', height: '22px' }}
                            >
                                <div
                                    className={`absolute top-0.5 rounded-full bg-white shadow transition-transform ${
                                        localFilters.excludeBestBookmakers.enabled ? 'translate-x-5' : 'translate-x-0.5'
                                    }`}
                                    style={{ width: '18px', height: '18px' }}
                                />
                            </button>
                        </div>

                        {localFilters.excludeBestBookmakers.enabled && bookmakers && bookmakers.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-2.5">
                                    Select bookmakers to exclude
                                    {localFilters.excludeBestBookmakers.value.length > 0 && (
                                        <span className="text-emerald-400 ml-1">
                                            ({localFilters.excludeBestBookmakers.value.length} selected)
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {bookmakers.map(bk => {
                                        const isExcluded = localFilters.excludeBestBookmakers.value.includes(bk);
                                        const logo = bookmakerLogos?.[bk];
                                        const name = bookmakerNames?.[bk] || bk;
                                        return (
                                            <button
                                                key={bk}
                                                onClick={() => handleExcludeBookmakerClick(bk)}
                                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                                    isExcluded
                                                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                                        : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600'
                                                }`}
                                                title={isExcluded ? `Excluding ${name}` : name}
                                            >
                                                {logo ? (
                                                    <img
                                                        src={logo}
                                                        alt={name}
                                                        className={`w-5 h-5 rounded object-cover ${isExcluded ? 'opacity-50' : ''}`}
                                                    />
                                                ) : (
                                                    <span>{name}</span>
                                                )}
                                                {isExcluded && (
                                                    <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-5 border-t border-slate-700/50">
                    <button
                        onClick={handleReset}
                        className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                    >
                        Reset All
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-medium hover:from-emerald-500 hover:to-teal-500 transition-all"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default FiltersModal;
